import { Card, Nav, Tab } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import crudApi from "@serie3/common/api/crudApi";
import { useEffect, useState } from "react";
import GroupConfig from "@serie3/common/Modules/Groups/Resources/Endpoints";
import { useTranslation } from "react-i18next";
import TabTitle from "../../../shared/Tabs/TabTitle";
import Posts from "./Tabs/Posts";
import Settings from "./Tabs/Settings/Settings";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import listHandler from "@serie3/common/Domain/listHandler";
import GroupDashboard from "./Tabs/GroupDashboard";
import {
  computeDashboardTotals,
  toTotalStatistics,
} from "@serie3/mymediaplanner/Modules/Groups/Domain/Dashboard";
import CalendarWrapper from "../CalendarWrapper";
import GroupPillars from "./Tabs/GroupPillars";
import GroupNotesTab from "./Tabs/GroupNotesTab";
import GroupApprovalsTab from "./Tabs/GroupApprovalsTab";
import GroupFilesTab from "./Tabs/GroupFilesTab";
import GroupLogo from "../../../shared/GroupLogo";

function GroupDetail({ groups = [], setGroups }) {
  const [group, setGroup] = useState(null);
  const [shares, setShares] = useState([]);
  const [rights, setRights] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(true);
  const [monthYear, setMonthYear] = useState(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    return `${month}${year}`;
  });
  const [dashboardData, setDashboardData] = useState([]);
  const [totalStatistics, setTotalStatistics] = useState(null);
  const [mostLikedPost, setMostLikedPost] = useState(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const { groupId } = useParams();
  // ?tab=approvals (from the Approvals page) opens that tab directly.
  const [searchParams] = useSearchParams();
  const initialTab = ["dashboard", "pillars", "posts", "groupCalendar", "notes", "files", "approvals", "settings"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "posts";

  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    setGroup(null);
    setShares([]);
    setRights(null);

    const fetchGroup = crudApi.justGet(GroupConfig.endpoints.show + groupId)
      .then((r) => { if (r.ok) { setGroup(r.data); setGroupName(r.data.name); } });

    const fetchRights = crudApi.justGet(GroupConfig.endpoints.getRights + groupId)
      .then((r) => { if (r.ok) setRights(r.data); });

    const fetchPosts = crudApi.justGet(MediaplannerGroupEndpoints.endpoints.listPosts + groupId)
      .then((r) => { if (r.ok) setShares(r.data); });

    Promise.all([fetchGroup, fetchRights, fetchPosts])
      .finally(() => setLoading(false));
  }, [groupId]);

  
  useEffect(() => {
    if (!groupId) return;
    

    let cancelled = false;

    const fetchDashboardData = async () => {
      const url =
        MediaplannerGroupEndpoints.endpoints.createdDashboard +
        "/" +
        groupId +
        "/" +
        monthYear;

      const response = await crudApi.justGet(url);
      if (!response?.ok || cancelled) return;

      const data = response.data ?? [];

      setDashboardData(data);

      // PURE CALCULATION (logic you “keep from Dashboard”)
      const totals = computeDashboardTotals(data);
      setTotalStatistics(toTotalStatistics(totals));
      setMostLikedPost(totals.mostLikedPost);
    };

    fetchDashboardData();

    return () => {
      cancelled = true;
    };
  }, [groupId, monthYear]);

  const deletePost = async (share) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.deletePostFromGroup + group.id + "/",
      share.id,
    );
    if (response.ok) {
      const tmpPosts = listHandler.deleteWithoutCall(share, shares);
      setShares([...tmpPosts]);
    }
  };

  return (
    <>
      <h4 className="page-section-header">
        <GroupLogo
          groupId={group?.id}
          version={logoVersion}
          className="page-section-header-icon"
          style={{ width: undefined, height: undefined, background: "rgba(86,74,177,0.12)", color: "#564ab1", fontSize: 16, fontWeight: 800, letterSpacing: "-0.5px" }}
          fallback={groupName
            ? groupName.trim().split(/\s+/).length > 1
              ? groupName.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join("")
              : groupName.slice(0, 2).toUpperCase()
            : "…"}
        />
        {group ? groupName : (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span className="btn-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
            <span style={{ color: "#9299a8", fontWeight: 400, fontSize: 14 }}>{t("Common.Loading")}</span>
          </span>
        )}
      </h4>

      <Tab.Container defaultActiveKey={initialTab}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item><Nav.Link eventKey="dashboard"><TabTitle icon="uil-dashboard" label={t("Backend.Groups.Tabs.Dashboard")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="pillars"><TabTitle icon="uil-layer-group" label={t("Backend.Groups.Tabs.Pillars", "Pillars")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="posts"><TabTitle icon="uil-file-alt" label={t("Backend.Groups.Tabs.Posts")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="groupCalendar"><TabTitle icon="uil-calendar-alt" label={t("Backend.Groups.Tabs.Calendar")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="notes"><TabTitle icon="uil-notes" label="Notes" /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="files"><TabTitle icon="uil-folder" label={t("Backend.Groups.Tabs.Files", "Files")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="approvals"><TabTitle icon="uil-check-circle" label={t("Backend.Groups.Tabs.Approvals", "Approvals")} /></Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="settings"><TabTitle icon="uil-cog" label={t("Backend.Groups.Tabs.Settings")} /></Nav.Link></Nav.Item>
        </Nav>

        {loading ? (
          <Card>
            <Card.Body style={{ padding: 24 }}>
              {/* Header row skeleton */}
              <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
                {[180, 120, 100, 140].map((w, i) => (
                  <div key={i} className="skeleton" style={{ height: 64, width: w, borderRadius: 12 }} />
                ))}
              </div>
              {/* Content rows skeleton */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton" style={{ height: 80, borderRadius: 10, marginBottom: 12 }} />
              ))}
            </Card.Body>
          </Card>
        ) : (

        <Tab.Content>
          <Tab.Pane eventKey="dashboard">
            <Card><Card.Body>
              {group && (
                <GroupDashboard
                  dashboardData={dashboardData}
                  totalStatistics={totalStatistics}
                  mostLikedPost={mostLikedPost}
                  group={group}
                  shares={shares}
                />
              )}
            </Card.Body></Card>
          </Tab.Pane>
          <Tab.Pane eventKey="posts">
            <Posts
              shares={shares}
              setShares={setShares}
              group={group}
              rights={rights}
              deletePost={deletePost}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="groupCalendar">
            <CalendarWrapper
              endpoint={`${MediaplannerGroupEndpoints.endpoints.getGroupSchedules}${groupId}/`}
              hideTitle
            />
          </Tab.Pane>
          <Tab.Pane eventKey="pillars">
            <Card><Card.Body>
              <GroupPillars group={group} rights={rights} />
            </Card.Body></Card>
          </Tab.Pane>
          <Tab.Pane eventKey="notes">
            <Card><Card.Body>
              <GroupNotesTab group={group} />
            </Card.Body></Card>
          </Tab.Pane>
          <Tab.Pane eventKey="files">
            {group && <GroupFilesTab group={group} />}
          </Tab.Pane>
          <Tab.Pane eventKey="approvals">
            {group && <GroupApprovalsTab group={group} />}
          </Tab.Pane>
          <Tab.Pane eventKey="settings">
            <Card><Card.Body>
              <Settings
                rights={rights}
                group={group}
                groups={groups}
                setGroups={setGroups}
                setGroupName={setGroupName}
                onLogoChanged={() => setLogoVersion((v) => v + 1)}
              />
            </Card.Body></Card>
          </Tab.Pane>
        </Tab.Content>
        )}
      </Tab.Container>
    </>
  );
}

export default GroupDetail;
